import type { PaymentLogRow } from '../types/utils'
import { buildDashboardReportPath } from '@/features/reports/utils/reportRoutePaths'
/** Payment log row actions (Angular view/edit/delete visibility). */


const INVOICE_TYPES_WITH_VIEW_WITHOUT_LOG_ID = [
  "CONTRACT_INVOICE",
  "APPLICATION_FEE_INVOICE",
  "BALANCE_ADJUSTMENT",
  "DEFAULT_INVOICE",
] as const;

const EDIT_TYPES_WITH_SOURCE = [
  "APPLICATION_FEE",
  "CONTRACT",
  "CONTRACT_INVOICE",
  "APPLICATION_FEE_INVOICE",
] as const;

const EDIT_TYPES_DEFAULT = ["DEFAULT", "DEFAULT_INVOICE"] as const;

const EDIT_TYPES_WITHOUT_SOURCE = [
  "CONTRACT_CHARGEBACK",
  "CHGBACK_APP_FEE",
  "CHGBACK_CONTRACT",
  "CONTRACT_INVOICE",
  "APPLICATION_FEE_INVOICE",
  "BALANCE_ADJUSTMENT",
] as const;

const CHARGEBACK_EDIT_TYPES = [
  "CONTRACT_CHARGEBACK",
  "CHGBACK_APP_FEE",
  "CHGBACK_CONTRACT",
] as const;

const DELETE_TYPES_WITHOUT_SOURCE = [
  "APPLICATION_FEE_INVOICE",
  "CONTRACT_INVOICE",
  "CONTRACT_CHARGEBACK",
  "CHGBACK_APP_FEE",
  "CHGBACK_CONTRACT",
] as const;

const DELETE_EXCLUDED_COMPLETED_TYPES = [
  "APPLICATION_FEE",
  "CONTRACT",
  "APPLICATION_FEE_INVOICE",
  "CONTRACT_INVOICE",
] as const;

function paymentType(payment: PaymentLogRow): string {
  return String(payment.payment_type ?? "").trim();
}

function paymentSource(payment: PaymentLogRow): string {
  return String(payment.payment_source ?? "").trim();
}

/** Angular view icon: hidden when no log id unless invoice adjustment type. */
export function canViewPaymentLogRow(payment: PaymentLogRow): boolean {
  if (payment.payment_log_id != null && payment.payment_log_id !== "") {
    return true;
  }
  const t = paymentType(payment);
  return (INVOICE_TYPES_WITH_VIEW_WITHOUT_LOG_ID as readonly string[]).includes(t);
}

/** Angular route used by the payment-log view icon. */
export function getPaymentLogDetailRoute(payment: PaymentLogRow): string | null {
  if (!canViewPaymentLogRow(payment)) return null;
  if (payment.pa_id != null && payment.pa_id !== "") {
    return `/reports/payment-logs/detail/${payment.pa_id}`;
  }
  if (payment.payment_log_id != null && payment.payment_log_id !== "") {
    return `/reports/payment-logs/${payment.payment_log_id}`;
  }
  return null;
}

export function isChargebackPaymentType(payment: PaymentLogRow): boolean {
  return (CHARGEBACK_EDIT_TYPES as readonly string[]).includes(paymentType(payment));
}

/** Angular chargeback detail route opened from payment log edit. */
export function getChargebackDetailAngularRoute(
  applicantId: string | number,
  paId: string | number,
): string {
  return `/reports/chargeback/detail/${encodeURIComponent(String(applicantId))}/${encodeURIComponent(String(paId))}`;
}

/** React chargeback detail route when the reports module is enabled. */
export function getChargebackDetailReactPath(
  applicantId: string | number,
  paId: string | number,
): string {
  return buildDashboardReportPath(getChargebackDetailAngularRoute(applicantId, paId));
}

/** Angular pencil `refundPayment` visibility (opens adjustment / external payment modal). */
export function canEditPaymentLogRow(payment: PaymentLogRow): boolean {
  const t = paymentType(payment);
  const src = paymentSource(payment);
  if ((EDIT_TYPES_WITH_SOURCE as readonly string[]).includes(t) && Boolean(src)) {
    return true;
  }
  if ((EDIT_TYPES_DEFAULT as readonly string[]).includes(t)) {
    return true;
  }
  if ((EDIT_TYPES_WITHOUT_SOURCE as readonly string[]).includes(t) && !src) {
    return true;
  }
  if (
    (t === "CONTRACT_REFUND_PARTIAL" || t === "CONTRACT_REFUND_FULL") &&
    String(payment.payment_status ?? "").trim() === "Rejected"
  ) {
    return true;
  }
  return false;
}

/** Angular trash `deletePayment` visibility. */
export function canDeletePaymentLogRow(payment: PaymentLogRow): boolean {
  const status = String(payment.payment_status ?? "").trim();
  if (status === "Cancelled") return false;

  const t = paymentType(payment);
  const src = paymentSource(payment);

  if ((DELETE_TYPES_WITHOUT_SOURCE as readonly string[]).includes(t) && !src) {
    return true;
  }
  if (src.includes("EXTERNAL_PAYMENT")) {
    return true;
  }
  if (
    !(DELETE_EXCLUDED_COMPLETED_TYPES as readonly string[]).includes(t) &&
    status !== "Completed"
  ) {
    return true;
  }
  return false;
}
